import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  ArrowDownToLine,
  ArrowUpToLine,
  Timer,
  TrendingUp,
  Lock,
  AlertTriangle,
  ShieldCheck,
} from 'lucide-react';
import { exchangeAPI, rateProtectionAPI } from '@/lib/api';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { toast } from 'sonner';

export default function Exchange() {
  const { t, lang } = useLanguage();
  const [rates, setRates] = useState(null);
  const [protection, setProtection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tradeDirection, setTradeDirection] = useState('buy');
  const [tradeAsset, setTradeAsset] = useState('trx');
  const [tradeAmount, setTradeAmount] = useState('');
  const [tradeLoading, setTradeLoading] = useState(false);

  const loadRates = useCallback(async () => {
    try {
      const [ratesRes, protRes] = await Promise.all([
        exchangeAPI.getRates(),
        rateProtectionAPI.getStatus().catch(() => ({ data: null })),
      ]);
      setRates(ratesRes.data);
      if (protRes.data) setProtection(protRes.data);
    } catch (err) {
      console.error('Rates load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRates();
    const interval = setInterval(loadRates, 30000);
    return () => clearInterval(interval);
  }, [loadRates]);

  const handleTrade = async () => {
    if (!tradeAmount || Number(tradeAmount) <= 0) {
      toast.error(t('exchange.validAmount'));
      return;
    }
    setTradeLoading(true);
    try {
      const res = await exchangeAPI.trade({
        direction: tradeDirection,
        asset: tradeAsset,
        amount: Number(tradeAmount),
      });
      toast.success(res.data.message);
      setTradeAmount('');
      await loadRates();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('exchange.tradeFailed'));
    } finally {
      setTradeLoading(false);
    }
  };

  const rateLockExpiry = rates?.rate_lock?.expires_at ? new Date(rates.rate_lock.expires_at) : null;
  const hoursLeft = rateLockExpiry ? Math.max(0, Math.round((rateLockExpiry - new Date()) / (1000 * 60 * 60))) : 0;
  const trxUsd = rates?.rate_lock?.trx_usd_rate || 0.25;
  const feePercent = rates?.fees?.trade_fee_percent || 0.5;

  const calculateEstimate = () => {
    const amount = Number(tradeAmount) || 0;
    if (tradeDirection === 'buy' && tradeAsset === 'trx') {
      const cost = amount * trxUsd; const fee = cost * (feePercent / 100);
      return { cost: cost + fee, receive: amount, costLabel: 'USDT', receiveLabel: 'TRX', fee };
    } else if (tradeDirection === 'sell' && tradeAsset === 'trx') {
      const received = amount * trxUsd; const fee = received * (feePercent / 100);
      return { cost: amount, receive: received - fee, costLabel: 'TRX', receiveLabel: 'USDT', fee };
    } else if (tradeDirection === 'buy' && tradeAsset === 'usdt') {
      const trxCost = amount / trxUsd; const fee = trxCost * (feePercent / 100);
      return { cost: trxCost + fee, receive: amount, costLabel: 'TRX', receiveLabel: 'USDT', fee };
    } else {
      const trxReceived = amount / trxUsd; const fee = trxReceived * (feePercent / 100);
      return { cost: amount, receive: trxReceived - fee, costLabel: 'USDT', receiveLabel: 'TRX', fee };
    }
  };

  const estimate = calculateEstimate();

  if (loading) {
    return (
      <DashboardLayout title={t('exchange.title')}>
        <div className="space-y-6">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('exchange.title')}>
      <div className="space-y-6">
        {/* Rate Lock Panel */}
        <Card data-testid="exchange-rate-lock-panel" className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-[hsl(var(--primary))]" />
                <span className="font-semibold font-['Space_Grotesk'] text-lg">{t('exchange.rateLock24h')}</span>
              </div>
              <Badge className="bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20 hover:bg-[rgba(45,211,111,0.18)]">
                <Timer className="w-3 h-3 mr-1" /> {hoursLeft}{t('dashboard.hoursLeft')}
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">{t('exchange.lockedRateTrx')}</p>
                <p className="text-2xl font-semibold font-['Space_Grotesk'] tabular-nums">${trxUsd.toFixed(4)}</p>
              </div>
              <div>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">{t('exchange.lockedRateUsdt')}</p>
                <p className="text-2xl font-semibold font-['Space_Grotesk'] tabular-nums">{rates?.rate_lock?.locked_rate?.toFixed(2)} TL</p>
              </div>
              <div>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-1">{t('exchange.lockStatus')}</p>
                <Progress value={(hoursLeft / 24) * 100} className="h-2 mt-3" />
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t('exchange.lockProtected')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Rate Protection Status */}
        {protection && (
          <Card data-testid="exchange-rate-protection" className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="w-5 h-5 text-[hsl(var(--primary))]" />
                  <span className="font-semibold font-['Space_Grotesk']">{t('exchange.rateProtection')}</span>
                </div>
                <Badge className={
                  protection.is_active
                    ? 'bg-[rgba(45,211,111,0.12)] text-[#2DD36F] border-[#2DD36F]/20'
                    : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
                }>
                  {protection.is_active ? t('exchange.protectionActive') : t('exchange.protectionInactive')}
                </Badge>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{t('exchange.protectionPercent')}</p>
                  <p className="font-semibold tabular-nums">%{protection.protection_percent}</p>
                </div>
                <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{t('exchange.maxProtected')}</p>
                  <p className="font-semibold tabular-nums">${protection.max_protected_amount?.toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{t('exchange.protectionUsed')}</p>
                  <p className="font-semibold tabular-nums">${protection.user_protection?.protection_used?.toFixed(2)}</p>
                </div>
                <div className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1">{t('exchange.protectionAvailable')}</p>
                  <p className="font-semibold tabular-nums text-[#2DD36F]">${protection.user_protection?.protection_available?.toFixed(2)}</p>
                </div>
              </div>
              <div className="mt-3">
                <Progress value={((protection.user_protection?.protection_used || 0) / (protection.max_protected_amount || 10000)) * 100} className="h-1.5" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trading Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="font-['Space_Grotesk']">{tradeDirection === 'buy' ? t('exchange.buyOrder') : t('exchange.sellOrder')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <Tabs value={tradeDirection} onValueChange={setTradeDirection}>
                <TabsList className="bg-[hsl(var(--secondary))] w-full">
                  <TabsTrigger value="buy" data-testid="exchange-buy-tab" className="flex-1 data-[state=active]:bg-[rgba(45,211,111,0.15)] data-[state=active]:text-[#2DD36F]">
                    <ArrowDownToLine className="w-4 h-4 mr-2" /> {t('exchange.buy')}
                  </TabsTrigger>
                  <TabsTrigger value="sell" data-testid="exchange-sell-tab" className="flex-1 data-[state=active]:bg-[rgba(217,74,74,0.15)] data-[state=active]:text-[#D94A4A]">
                    <ArrowUpToLine className="w-4 h-4 mr-2" /> {t('exchange.sell')}
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('common.asset')}</Label>
                  <Select value={tradeAsset} onValueChange={setTradeAsset}>
                    <SelectTrigger className="bg-[hsl(var(--background))] border-[hsl(var(--border))]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <SelectItem value="trx">TRX (Tron)</SelectItem>
                      <SelectItem value="usdt">USDT (Tether)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('common.amount')} ({tradeAsset.toUpperCase()})</Label>
                  <Input type="number" value={tradeAmount} onChange={(e) => setTradeAmount(e.target.value)} placeholder="0.00" className="bg-[hsl(var(--background))] border-[hsl(var(--border))] text-lg h-12" />
                </div>

                <Button
                  data-testid="exchange-place-order-button"
                  onClick={handleTrade}
                  disabled={tradeLoading || !tradeAmount}
                  className={`w-full h-12 text-base active:scale-[0.98] transition-colors duration-200 ${
                    tradeDirection === 'buy'
                      ? 'bg-[#2DD36F] hover:bg-[#2DD36F]/90 text-[hsl(var(--background))]'
                      : 'bg-[#D94A4A] hover:bg-[#D94A4A]/90 text-white'
                  }`}
                >
                  {tradeLoading ? t('common.processing') : tradeDirection === 'buy' ? `${tradeAsset.toUpperCase()} ${t('exchange.buy')}` : `${tradeAsset.toUpperCase()} ${t('exchange.sell')}`}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Estimate Card */}
          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="font-['Space_Grotesk'] text-base">{t('exchange.tradeSummary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-[hsl(var(--muted-foreground))]">{t('exchange.rate')}</span>
                  <span className="tabular-nums">1 TRX = ${trxUsd.toFixed(4)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[hsl(var(--muted-foreground))]">{t('exchange.commission')}</span>
                  <span className="tabular-nums">%{feePercent}</span>
                </div>
                {tradeAmount && Number(tradeAmount) > 0 && (
                  <>
                    <div className="border-t border-[hsl(var(--border))] pt-3" />
                    <div className="flex justify-between text-sm">
                      <span className="text-[hsl(var(--muted-foreground))]">{t('exchange.youPay')}</span>
                      <span className="tabular-nums font-medium">{estimate.cost.toFixed(4)} {estimate.costLabel}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[hsl(var(--muted-foreground))]">{t('common.fee')}</span>
                      <span className="tabular-nums text-[#F7C84B]">{estimate.fee.toFixed(4)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-semibold">
                      <span>{t('exchange.youReceive')}</span>
                      <span className="tabular-nums text-[#2DD36F]">{estimate.receive.toFixed(4)} {estimate.receiveLabel}</span>
                    </div>
                  </>
                )}
              </div>
              <div className="bg-[hsl(var(--secondary))] rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#F7C84B] mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('exchange.lockWarning')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Market Prices */}
        <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="font-['Space_Grotesk']">{t('exchange.marketPrices')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {rates?.prices && Object.entries(rates.prices).map(([key, val]) => (
                <div key={key} className="p-3 rounded-lg bg-[hsl(var(--secondary))]">
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1 capitalize">{key}</p>
                  <p className="font-semibold tabular-nums">${val.usd?.toLocaleString('en-US', { maximumFractionDigits: 4 })}</p>
                  <p className={`text-xs tabular-nums ${val.usd_24h_change >= 0 ? 'text-[#2DD36F]' : 'text-[#D94A4A]'}`}>
                    {val.usd_24h_change >= 0 ? '+' : ''}{val.usd_24h_change?.toFixed(2)}%
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
